import { useCallback, useEffect, useMemo } from "react";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import {
  useRequestWithdrawable,
  useResolvedTypedAddress,
} from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { guards } from "@sablier/v2-machines";
import { _ } from "@sablier/v2-mixins";
import { vendors } from "@sablier/v2-utils";
import {
  useAccount,
  useChain,
  useCoverWithdraw,
  useMachineWithdraw,
  useModalTransaction,
  useResetter,
  useStreamCurrent,
  useToken,
  useWarning,
  useWithdrawAccessor,
  useWithdrawField,
  useWithdrawForm,
} from "~/client/hooks";

export function useInit() {
  const { stream, isReady } = useStreamCurrent();
  const { value: max, isLoading } = useRequestWithdrawable({ stream });
  const update = useWithdrawForm((state) => state.api.updateFields);
  const token = useToken({
    token: stream?.token,
  });

  useEffect(() => {
    if (isReady) {
      update({
        amount: {
          isLoading,
        },
        max: {
          value: max?.humanized.toString(),
        },
        token: {
          value: token,
        },
      });
    }
  }, [isReady, isLoading, stream, max, token, update]);
}

export function useFormPrepare() {
  const { t } = useT();
  const fields = useWithdrawForm((state) => state.fields);
  const { isReady, stream } = useStreamCurrent();
  const { machine, send } = useMachineWithdraw();
  const { state } = useWarning("withdraw", stream);
  const error = useMemo(() => machine.context.error, [machine.context.error]);
  const isPrepared = useMemo(
    () => _.isNilOrEmptyString(error) && isReady && state === "ready",
    [error, isReady, state],
  );

  useEffect(() => {
    send("CHECK", { payload: { api: { t }, fields } });
  }, [fields, t, send]);

  return {
    isPrepared,
  };
}

export function useFieldAddress() {
  const { chain } = useChain();
  const access = useWithdrawAccessor();
  const address = useWithdrawField("address");
  const resolution = useResolvedTypedAddress(
    address.field.value,
    chain?.chainId,
  );
  const { t } = useT();

  const label = useMemo(
    () => ({
      icon: QuestionMarkCircleIcon,
      isIconLast: true,
      tooltip: {
        value: t("errors.addressAbility"),
        arrow: true,
      },
      value: t("modalCovers.withdraw.address"),
    }),
    [t],
  );

  const onBlur = useCallback(() => {
    const state = access();
    const chainId = chain?.chainId;
    const field = state.fields.address;
    const update = state.api.updateFields;

    if (_.isNilOrEmptyString(field.value)) {
      update({
        address: {
          isFocused: false,
          warning: undefined,
        },
      });
    } else {
      update({
        address: {
          isFocused: false,
          warning: guards.validateAccount({
            t,
            chainId,
            value: field.value,
          }),
        },
      });
    }
  }, [access, chain, t]);

  const onFocus = useCallback(() => {
    access().api.updateFields({ address: { isFocused: true } });
  }, [access]);

  const onChange = useCallback(
    (value: string | undefined) => {
      const state = access();
      const update = state.api.updateFields;
      update({ address: { value, resolution: undefined, warning: undefined } });
    },
    [access],
  );

  /** --- When the chain is changed, re-test the values --- */
  useEffect(() => {
    const state = access();
    const field = state.fields.address;
    if (!_.isNilOrEmptyString(field.value)) {
      onBlur();
    }
  }, [access, chain, onBlur]);

  /** --- When resolution happens, move its values in the store --- */
  useEffect(() => {
    const state = access();
    const update = state.api.updateFields;
    update({
      address: {
        isLoading: resolution.isLoading,
        resolution: _.pick(resolution, ["address", "ens", "preview", "short"]),
      },
    });
  }, [access, resolution]);

  /** --- When the field moves out-of-focus and the resolution is ready, validate it --- */
  useEffect(() => {
    if (
      !address.field.isFocused &&
      !address.field.isLoading &&
      !_.isNil(address.field.resolution)
    ) {
      const state = access();
      const chainId = chain?.chainId;
      const update = state.api.updateFields;

      if (!_.isNilOrEmptyString(address.field.value)) {
        update({
          address: {
            warning: guards.validateAccount({
              t,
              address: address.field.resolution.address,
              chainId,
              isResolved: true,
              value: address.field.value,
            }),
          },
        });
      }
    }
  }, [access, address, chain, t]);

  return {
    address,
    isLoading: resolution.isLoading,
    label,
    onBlur,
    onChange,
    onFocus,
  };
}

export function useFieldAmount() {
  const access = useWithdrawAccessor();
  const amount = useWithdrawField("amount");
  const max = useWithdrawField("max");
  const token = useWithdrawField("token");
  const { t } = useT();

  const maximum = useMemo(
    () => ({
      label: "Max:",
      value: max.field.value
        ? _.toNumeralPrice(max.field.value, false)
        : undefined,
    }),
    [max],
  );

  const onBlur = useCallback(() => {
    const state = access();
    const max = state.fields.max.value;
    const value = state.fields.amount.value;
    const update = state.api.updateFields;

    if (_.isNilOrEmptyString(value)) {
      update({ amount: { warning: undefined } });
    } else {
      update({
        amount: {
          warning: guards.validateAmount({
            t,
            context: "stream",
            max,
            min: "0",
            value,
          }),
        },
      });
    }
  }, [access, t]);

  const onChange = useCallback(
    (value: string | undefined) => {
      const state = access();
      const update = state.api.updateFields;
      update({ amount: { value, warning: undefined } });
    },
    [access],
  );

  const onMax = useCallback(() => {
    const state = access();
    const update = state.api.updateFields;
    vendors.track.log((events) => events.clickMax("withdrawCover"));
    update({
      amount: {
        value: state.fields.max.value,
      },
    });
    onBlur();
  }, [access, onBlur]);

  useEffect(() => {
    onBlur();
  }, [onBlur, max]);

  return {
    amount,
    maximum,
    onBlur,
    onChange,
    onMax,
    token,
  };
}

export function useEnableAddress() {
  const access = useWithdrawAccessor();
  const address = useWithdrawField("address");

  const isActive = useMemo(() => address.field.isActive, [address]);

  const enableAddress = useCallback(() => {
    vendors.track.log("useDifferentWithdrawAddress");
    const state = access();
    const update = state.api.updateFields;
    update({ address: { isActive: true } });
  }, [access]);

  return {
    isActive,
    enableAddress,
  };
}

export function useFormSubmit() {
  const access = useWithdrawAccessor();
  const { machine, send } = useMachineWithdraw();
  const { setOpen, updateData } = useModalTransaction();
  const { doClose } = useCoverWithdraw();
  const { proxy, signer } = useAccount();
  const { isReady, stream } = useStreamCurrent();
  const { all: reset } = useResetter();
  const error = useMemo(() => machine.context.error, [machine.context.error]);
  const { t } = useT();

  const isSubmitting = useMemo(
    () => machine.value === "process" || machine.value === "validate",
    [machine.value],
  );

  const doSubmit = useCallback(() => {
    const state = access();
    if (isReady) {
      send("SAVE", {
        payload: {
          api: {
            t,
            setOpen,
            updateData,
            reset: () => {
              doClose();
              void reset();
            },
          },
          fields: state.fields,
          proxy,
          signer,
          stream,
        },
      });
    }
  }, [
    access,
    doClose,
    isReady,
    proxy,
    reset,
    send,
    setOpen,
    stream,
    signer,
    updateData,
    t,
  ]);

  return {
    doSubmit,
    error,
    isSubmitting,
  };
}
